import { C } from '../theme/palette';

/**
 * Grille tarifaire du bâtiment.
 *
 * Les quatre paliers ne se distinguent pas par les fonctions — elles sont
 * toutes incluses partout — mais par le nombre d'utilisateurs et d'employés
 * qu'ils autorisent. Vendre une fonction en moins ferait douter de celles qui
 * restent ; vendre de la capacité se comprend sans explication.
 *
 * `users` et `emp` sont les plafonds du palier. Le serveur les applique à
 * l'ajout d'un compte du bureau ou d'un employé, dès qu'une formule est
 * choisie ; une société qui n'en a pas encore choisi n'est pas plafonnée.
 */
export const PLANS_BAT = [
  {
    id: 'solo',
    nom: 'Bronze',
    prix: 39,
    users: 2,
    emp: 4,
    ic: '👤',
    col: C.teal,
    feats: [
      'Toutes les fonctions incluses',
      'Salaires inclus · chantiers/magasin',
      'App mobile · scan gratuit',
      '1 entreprise',
    ],
  },
  {
    id: 'equipe',
    nom: 'Argent',
    prix: 59,
    users: 4,
    emp: 6,
    ic: '👥',
    col: C.green,
    feats: [
      'Toutes les fonctions incluses',
      'Salaires inclus',
      'Support prioritaire',
      '1 entreprise',
    ],
  },
  {
    id: 'pro',
    nom: 'Platine',
    prix: 89,
    users: 6,
    emp: 9,
    ic: '🏢',
    col: C.purple,
    feats: [
      'Toutes les fonctions incluses',
      'Salaires inclus',
      'Support prioritaire',
      '1 entreprise',
    ],
  },
  {
    id: 'entreprise',
    nom: 'Or',
    prix: 129,
    users: 30,
    emp: 35,
    ic: '🏛️',
    col: C.navy,
    feats: [
      'Toutes les fonctions incluses',
      'Salaires inclus',
      'Support prioritaire + téléphone',
      '1 entreprise',
    ],
  },
];

/** Le magasin est facturé comme le bâtiment : même grille, sans copie. */
export const PLANS_MAG = PLANS_BAT;

/**
 * Grille du mixte, posée palier par palier.
 *
 * Une entreprise qui tient chantier et magasin dans la même application ne
 * paie pas deux abonnements : elle utilise un seul jeu de données, une seule
 * comptabilité, une seule paie.
 *
 * Ces prix sont choisis, non calculés. Ils dérivaient d'une majoration de
 * moitié appliquée au bâtiment, ce qui liait deux décisions commerciales qui
 * n'ont pas de raison d'aller ensemble : changer le prix du bâtiment déplaçait
 * silencieusement celui du mixte. Chaque palier porte donc désormais son
 * montant.
 */
const FEATS_MIX = (dernier) => [
  'Bâtiment + Magasin réunis',
  'Salaires inclus',
  dernier,
  '1 entreprise',
];

export const PLANS_MIX = [
  {
    id: 'solo',
    nom: 'Bronze',
    prix: 70,
    users: 2,
    emp: 4,
    ic: '👤',
    col: C.teal,
    feats: FEATS_MIX('Chantiers + stock/caisse/ventes'),
  },
  {
    id: 'equipe',
    nom: 'Argent',
    prix: 84,
    users: 4,
    emp: 6,
    ic: '👥',
    col: C.green,
    feats: FEATS_MIX('Support prioritaire'),
  },
  {
    id: 'pro',
    nom: 'Platine',
    prix: 192,
    users: 6,
    emp: 9,
    ic: '🏢',
    col: C.purple,
    feats: FEATS_MIX('Support prioritaire'),
  },
  {
    id: 'entreprise',
    nom: 'Or',
    prix: 238,
    users: 30,
    emp: 35,
    ic: '🏛️',
    col: C.navy,
    feats: FEATS_MIX('Support prioritaire + téléphone'),
  },
];

/**
 * Remise consentie sur un paiement à l'année.
 *
 * <p>Un an payé d'avance vaut une avance de trésorerie et un client qui reste :
 * la remise le reconnaît. Elle vit ici pour que le prix annoncé, le prix
 * facturé et le prix du serveur ne puissent pas diverger.
 */
export const REMISE_ANNUELLE = 0.1;

/**
 * Prix d'un palier pour la période choisie.
 *
 * @param {object} plan palier de la grille
 * @param {boolean} annuel vrai pour un paiement à l'année
 * @returns {number} le montant à payer, en francs entiers
 */
export function prixPeriode(plan, annuel) {
  const mensuel = Number(plan?.prix) || 0;
  return annuel ? Math.round(mensuel * 12 * (1 - REMISE_ANNUELLE)) : mensuel;
}

/**
 * Économie réalisée en payant à l'année plutôt que mois par mois.
 *
 * @param {object} plan palier de la grille
 * @returns {number} l'écart annuel, en francs entiers
 */
export function economieAnnuelle(plan) {
  const mensuel = Number(plan?.prix) || 0;
  return Math.round(mensuel * 12 * REMISE_ANNUELLE);
}

/**
 * Suppléments facturés en sus de l'abonnement.
 *
 * Une taille relève le plafond d'employés de la formule jusqu'à son effectif
 * (`employes`) ; elle ne touche pas aux utilisateurs.
 *
 * Le pack IA porte une limite mensuelle parce qu'il appelle un service de
 * lecture facturé à l'usage : sans plafond, un client qui numérise tout son
 * archivage coûterait plus cher qu'il ne rapporte.
 */
export const OPTIONS_ST = {
  entreprise: [
    { taille: 'Petite (≤5 empl.)', prix: 25, employes: 5 },
    { taille: 'Moyenne (≤10 empl.)', prix: 39, employes: 10 },
    { taille: 'Grande (≤30 empl.)', prix: 65, employes: 30 },
  ],
  packIA: {
    prix: 9,
    limite: '200 factures + 100 devis / mois',
    nom: 'Pack IA (lecture auto factures + dictée vocale)',
  },
};

/**
 * Grille applicable à une société.
 *
 * Le secteur est porté par la fiche société, mais l'interface peut être en
 * vue mixte sans que la société le soit : les deux cas donnent la grille
 * mixte, faute de quoi le client verrait un tarif qu'il ne peut pas souscrire.
 */
export function grilleDuSecteur(secteur, estMixte) {
  if (estMixte || secteur === 'mixte') {
    return PLANS_MIX;
  }
  return secteur === 'magasin' ? PLANS_MAG : PLANS_BAT;
}
